import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AttachmentsSection } from './AttachmentsSection';
import { printClinicalRecord } from './clinical-record-print';
import {
  ADD_CLINICAL_ADDENDUM_MUTATION,
  ADD_CLINICAL_DIAGNOSIS_MUTATION,
  CLINICAL_VISIT_BY_ORDER_QUERY,
  CLOSE_CLINICAL_VISIT_MUTATION,
  PET_CLINICAL_SUMMARY_QUERY,
  REMOVE_CLINICAL_DIAGNOSIS_MUTATION,
  SAVE_CLINICAL_ANAMNESIS_MUTATION,
  SAVE_CLINICAL_PHYSICAL_EXAM_MUTATION,
  SAVE_CLINICAL_PLAN_TREATMENT_MUTATION,
  idempotencyContext,
} from './clinical.gql';
import { ClinicalHistoryModal } from './ClinicalHistoryModal';
import { ClinicalVisitStatusBadge } from './ClinicalVisitStatusBadge';
import { ImagingSection } from './ImagingSection';
import { LabsSection } from './LabsSection';
import { PrescriptionsSection } from './PrescriptionsSection';
import { useClinicIdentity } from './useClinicIdentity';
import { useRxPrintBase } from './useRxPrintBase';

import { hasPermission } from '@/modules/auth/useAuth';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { showToast } from '@/shared/ui/toast';
import { useAuthStore } from '@/stores/authStore';

const LOCKED = new Set(['closed', 'addended', 'cancelled', 'voided']);
const ADDENDABLE = new Set(['closed', 'addended']);
const TABS = ['anamnesis', 'exam', 'diagnoses', 'plan', 'prescriptions', 'labs', 'imaging', 'attachments', 'addendums'] as const;
type Tab = (typeof TABS)[number];

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-argument */

const KNOWN_ERROR_CODES = [
  'clinical_visit_locked',
  'clinical_visit_close_requires_diagnosis',
  'clinical_visit_close_requires_exam',
  'clinical_addendum_visit_not_closed',
  'clinical_addendum_empty',
  'clinical_diagnoses_reorder_id_mismatch',
  'clinical_visit_not_closeable',
  'clinical_visit_not_pendable',
  'clinical_visit_not_found',
  'clinical_diagnosis_not_found',
];

function errText(err: unknown, t: (k: string) => string): string {
  if (err instanceof ApolloError) {
    const raw = err.graphQLErrors[0]?.message ?? err.message;
    // Service codes often carry a `:<suffix>` (e.g. clinical_visit_not_closeable:draft).
    const base = raw.split(':')[0] ?? raw;
    if (KNOWN_ERROR_CODES.includes(base)) return t(`clinical.error_${base}`);
    return raw;
  }
  if (err instanceof Error) return err.message;
  return 'error';
}

function Area(props: {
  label: string;
  value: string;
  disabled?: boolean;
  onChange: (v: string) => void;
  onBlur?: () => void;
  rows?: number;
}): JSX.Element {
  return (
    <div className="space-y-1">
      <Label>{props.label}</Label>
      <textarea
        className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm disabled:opacity-60"
        rows={props.rows ?? 2}
        value={props.value}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.value)}
        onBlur={props.onBlur}
      />
    </div>
  );
}

export function ClinicalVisitPanel({ sale_order_id }: { sale_order_id: string }): JSX.Element | null {
  const { t } = useTranslation();
  const perms = useAuthStore((s) => s.user?.permissions ?? []);
  const can_write = hasPermission(perms, 'clinical.visits.write');
  const can_close = hasPermission(perms, 'clinical.visits.close');
  const can_diag = hasPermission(perms, 'clinical.diagnoses.write');
  const can_addendum = hasPermission(perms, 'clinical.visits.addendum');
  const can_attach = hasPermission(perms, 'clinical.attachments.write');
  const can_labs = hasPermission(perms, 'clinical.labs.write');
  const can_imaging = hasPermission(perms, 'clinical.imaging.write');
  const can_rx = hasPermission(perms, 'clinical.prescriptions.write');

  const { data, refetch } = useQuery(CLINICAL_VISIT_BY_ORDER_QUERY, {
    variables: { sale_order_id },
    skip: !sale_order_id,
    fetchPolicy: 'cache-and-network',
  });
  const detail = data?.clinicalVisitByOrderId ?? null;
  const visit = detail?.visit ?? null;
  const locked = visit ? LOCKED.has(visit.status) : true;
  // Attachments/results may be appended on closed/addended visits — only cancelled/voided block.
  const appendable = visit ? !['cancelled', 'voided'].includes(visit.status) : false;
  const editable = Boolean(visit) && !locked && can_write;

  const { data: summary_data } = useQuery(PET_CLINICAL_SUMMARY_QUERY, {
    variables: { pet_id: visit?.pet_id ?? '' },
    skip: !visit?.pet_id,
    fetchPolicy: 'cache-and-network',
  });
  const summary = summary_data?.petClinicalSummary ?? null;

  const branding = useClinicIdentity();
  const rx_base = useRxPrintBase({ pet_id: visit?.pet_id, visit });
  const rx_print_base = {
    patient_name: rx_base.patient_name,
    species: rx_base.species,
    vet_name: rx_base.vet_name,
    visit_folio: rx_base.visit_folio,
  };

  const onDownloadRecord = async (): Promise<void> => {
    if (!visit) return;
    const ok = await printClinicalRecord(
      branding,
      {
        pet_record_code: summary?.pet_record_code ?? null,
        patient_name: rx_base.patient_name,
        species: rx_base.species,
        breed: rx_base.breed,
        color: rx_base.color,
        sex: rx_base.sex,
        age_label: rx_base.age_label,
        weight_label: rx_base.weight_label,
        owner_name: rx_base.owner_name,
        owner_phone: rx_base.owner_phone,
        visit_date: new Date(visit.created_at ?? Date.now()).toLocaleDateString(),
        chief_complaint: detail?.anamnesis?.chief_complaint ?? visit.chief_complaint ?? null,
        anamnesis: detail?.anamnesis ?? null,
        physical_exam: detail?.physical_exam ?? null,
        diagnoses: detail?.diagnoses ?? [],
        plan_treatment: detail?.plan_treatment ?? null,
        addendums: detail?.addendums ?? [],
        vet_name: rx_base.vet_name,
      },
      t,
    );
    if (!ok) showToast({ tone: 'error', title: t('clinical.record_print_blocked') });
  };

  const [tab, setTab] = useState<Tab>('anamnesis');
  const [error, setError] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);

  // Section form state, hydrated from the loaded detail.
  const [anam, setAnam] = useState<Record<string, string>>({});
  const [exam, setExam] = useState<Record<string, string>>({});
  const [plan, setPlan] = useState<Record<string, string>>({});
  const [closeReasons, setCloseReasons] = useState({ diag: '', exam: '' });
  const [newDiag, setNewDiag] = useState({ kind: 'presumptive', description: '', is_primary: false });
  const [addendum, setAddendum] = useState('');

  // Hydrate the section forms once per visit. A save triggers refetch() which would
  // otherwise re-run this and clobber unsaved edits in a non-active tab. Status,
  // diagnoses and addenda still refresh on every render (they read `detail` directly).
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!detail || !visit) return;
    if (hydratedFor.current === visit.id) return;
    hydratedFor.current = visit.id;
    const a = detail.anamnesis ?? {};
    setAnam({
      chief_complaint: a.chief_complaint ?? '',
      symptom_onset_days: a.symptom_onset_days != null ? String(a.symptom_onset_days) : '',
      previous_illnesses: a.previous_illnesses ?? '',
      current_medications: a.current_medications ?? '',
      diet_notes: a.diet_notes ?? '',
      environment_notes: a.environment_notes ?? '',
      other_notes: a.other_notes ?? '',
    });
    const e = detail.physical_exam ?? {};
    const fallback_weight = detail.visit?.weight_grams_at_visit ?? null;
    setExam({
      body_weight_grams:
        e.body_weight_grams != null
          ? String(e.body_weight_grams)
          : fallback_weight != null
            ? String(fallback_weight)
            : '',
      body_temperature_celsius_tenths:
        e.body_temperature_celsius_tenths != null ? String(e.body_temperature_celsius_tenths) : '',
      heart_rate_bpm: e.heart_rate_bpm != null ? String(e.heart_rate_bpm) : '',
      respiratory_rate_bpm: e.respiratory_rate_bpm != null ? String(e.respiratory_rate_bpm) : '',
      body_condition_score: e.body_condition_score != null ? String(e.body_condition_score) : '',
      cardiovascular_notes: e.cardiovascular_notes ?? '',
      respiratory_notes: e.respiratory_notes ?? '',
      gastrointestinal_notes: e.gastrointestinal_notes ?? '',
      abdomen_notes: e.abdomen_notes ?? '',
      general_impression: e.general_impression ?? '',
      exam_not_performed_reason: e.exam_not_performed_reason ?? '',
    });
    const p = detail.plan_treatment ?? {};
    setPlan({
      treatment_notes: p.treatment_notes ?? '',
      medications_notes: p.medications_notes ?? '',
      diet_recommendations: p.diet_recommendations ?? '',
      activity_restrictions: p.activity_restrictions ?? '',
      follow_up_notes: p.follow_up_notes ?? '',
      owner_instructions: p.owner_instructions ?? '',
    });
  }, [detail]);

  const [saveAnam] = useMutation(SAVE_CLINICAL_ANAMNESIS_MUTATION);
  const [saveExam] = useMutation(SAVE_CLINICAL_PHYSICAL_EXAM_MUTATION);
  const [savePlan] = useMutation(SAVE_CLINICAL_PLAN_TREATMENT_MUTATION);
  const [addDiag] = useMutation(ADD_CLINICAL_DIAGNOSIS_MUTATION);
  const [removeDiag] = useMutation(REMOVE_CLINICAL_DIAGNOSIS_MUTATION);
  const [closeVisit] = useMutation(CLOSE_CLINICAL_VISIT_MUTATION);
  const [addAddendum] = useMutation(ADD_CLINICAL_ADDENDUM_MUTATION);

  const num = (v: string | undefined): number | undefined => {
    if (v == null) return undefined;
    const n = Number(v);
    return v.trim() === '' || Number.isNaN(n) ? undefined : n;
  };
  const txt = (v: string | undefined): string | undefined =>
    v == null || v.trim() === '' ? undefined : v;

  const run = async (fn: () => Promise<unknown>, successTitle?: string): Promise<void> => {
    try {
      setError(null);
      await fn();
      await refetch();
      // showToast dedupes by tone+title, so repeated save-on-blur confirmations collapse
      // to a single toast rather than spamming as the doctor tabs between fields.
      if (successTitle) showToast({ tone: 'success', title: successTitle });
    } catch (err) {
      setError(errText(err, t));
    }
  };

  if (!sale_order_id) return null;
  if (!visit) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('clinical.panel_title')}</CardTitle>
        </CardHeader>
        <CardContent className="text-muted-foreground text-sm">
          {t('clinical.no_visit')}
        </CardContent>
      </Card>
    );
  }

  const onSaveAnamnesis = (): Promise<void> =>
    run(() =>
      saveAnam({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            chief_complaint: txt(anam.chief_complaint),
            symptom_onset_days: num(anam.symptom_onset_days),
            previous_illnesses: txt(anam.previous_illnesses),
            current_medications: txt(anam.current_medications),
            diet_notes: txt(anam.diet_notes),
            environment_notes: txt(anam.environment_notes),
            other_notes: txt(anam.other_notes),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onSaveExam = (): Promise<void> =>
    run(() =>
      saveExam({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            body_weight_grams: num(exam.body_weight_grams),
            body_temperature_celsius_tenths: num(exam.body_temperature_celsius_tenths),
            heart_rate_bpm: num(exam.heart_rate_bpm),
            respiratory_rate_bpm: num(exam.respiratory_rate_bpm),
            body_condition_score: num(exam.body_condition_score),
            cardiovascular_notes: txt(exam.cardiovascular_notes),
            respiratory_notes: txt(exam.respiratory_notes),
            gastrointestinal_notes: txt(exam.gastrointestinal_notes),
            abdomen_notes: txt(exam.abdomen_notes),
            general_impression: txt(exam.general_impression),
            exam_not_performed_reason: txt(exam.exam_not_performed_reason),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onSavePlan = (): Promise<void> =>
    run(() =>
      savePlan({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            treatment_notes: txt(plan.treatment_notes),
            medications_notes: txt(plan.medications_notes),
            diet_recommendations: txt(plan.diet_recommendations),
            activity_restrictions: txt(plan.activity_restrictions),
            follow_up_notes: txt(plan.follow_up_notes),
            owner_instructions: txt(plan.owner_instructions),
          },
        },
        context: idempotencyContext(),
      }),
      t('clinical.toast_section_saved'),
    );

  const onAddDiagnosis = (): Promise<void> => {
    // Validate before run() so a rejected input never shows a success toast.
    if (newDiag.description.trim().length < 3) {
      setError(t('clinical.error_diagnosis_short'));
      return Promise.resolve();
    }
    return run(async () => {
      await addDiag({
        variables: {
          input: {
            clinical_visit_id: visit.id,
            diagnosis_kind: newDiag.kind,
            description: newDiag.description,
            is_primary: newDiag.is_primary,
          },
        },
        context: idempotencyContext(),
      });
      setNewDiag({ kind: 'presumptive', description: '', is_primary: false });
    }, t('clinical.toast_diagnosis_added'));
  };

  const onClose = (): Promise<void> =>
    run(
      () =>
        closeVisit({
          variables: {
            input: {
              clinical_visit_id: visit.id,
              has_no_diagnosis_reason: txt(closeReasons.diag),
              exam_not_performed_reason: txt(closeReasons.exam),
            },
          },
          context: idempotencyContext(),
        }),
      t('clinical.toast_visit_closed'),
    );

  const onAddAddendum = (): Promise<void> =>
    run(async () => {
      await addAddendum({
        variables: { input: { clinical_visit_id: visit.id, content: addendum } },
        context: idempotencyContext(),
      });
      setAddendum('');
    }, t('clinical.toast_addendum_added'));

  const diagnoses: any[] = detail.diagnoses ?? [];
  const addendums: any[] = detail.addendums ?? [];

  return (
    <Card>
      <CardHeader className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="font-display text-base font-bold tracking-tight">
            {t('clinical.panel_title')} · {visit.folio_number}
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowHistory(true)}>
              {t('clinical.view_record')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => void onDownloadRecord()}>
              {t('clinical.download_record')}
            </Button>
            <ClinicalVisitStatusBadge status={visit.status} />
          </div>
        </div>
        {summary ? (
          <p className="text-muted-foreground text-xs">
            {summary.pet_name}
            {summary.species ? ` · ${summary.species}` : ''} · {t('clinical.visit_count')}:{' '}
            {summary.visit_count} · {t('clinical.open_count')}: {summary.open_visit_count}
          </p>
        ) : null}
        {locked ? (
          <p className="text-muted-foreground text-xs italic">{t('clinical.read_only_hint')}</p>
        ) : null}
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-1">
          {TABS.map((tb) => (
            <Button
              key={tb}
              size="sm"
              variant={tab === tb ? 'default' : 'outline'}
              onClick={() => setTab(tb)}
            >
              {t(`clinical.tab_${tb}`)}
            </Button>
          ))}
        </div>

        {error ? (
          <div className="border-destructive/40 bg-destructive/5 text-destructive rounded-md border p-2 text-sm">
            {error}
          </div>
        ) : null}

        {tab === 'anamnesis' ? (
          <div className="space-y-2">
            <Area label={t('clinical.f_chief_complaint')} value={anam.chief_complaint ?? ''} disabled={!editable} onChange={(v) => setAnam({ ...anam, chief_complaint: v })} onBlur={editable ? onSaveAnamnesis : undefined} />
            <div className="space-y-1">
              <Label>{t('clinical.f_symptom_onset_days')}</Label>
              <Input type="number" value={anam.symptom_onset_days ?? ''} disabled={!editable} onChange={(e) => setAnam({ ...anam, symptom_onset_days: e.target.value })} onBlur={editable ? onSaveAnamnesis : undefined} />
            </div>
            <Area label={t('clinical.f_previous_illnesses')} value={anam.previous_illnesses ?? ''} disabled={!editable} onChange={(v) => setAnam({ ...anam, previous_illnesses: v })} onBlur={editable ? onSaveAnamnesis : undefined} />
            <Area label={t('clinical.f_current_medications')} value={anam.current_medications ?? ''} disabled={!editable} onChange={(v) => setAnam({ ...anam, current_medications: v })} onBlur={editable ? onSaveAnamnesis : undefined} />
            <Area label={t('clinical.f_diet_notes')} value={anam.diet_notes ?? ''} disabled={!editable} onChange={(v) => setAnam({ ...anam, diet_notes: v })} onBlur={editable ? onSaveAnamnesis : undefined} />
            <Area label={t('clinical.f_other_notes')} value={anam.other_notes ?? ''} disabled={!editable} onChange={(v) => setAnam({ ...anam, other_notes: v })} onBlur={editable ? onSaveAnamnesis : undefined} />
            {editable ? <Button size="sm" onClick={onSaveAnamnesis}>{t('clinical.save_section')}</Button> : null}
          </div>
        ) : null}

        {tab === 'exam' ? (
          <div className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              {(['body_weight_grams', 'body_temperature_celsius_tenths', 'heart_rate_bpm', 'respiratory_rate_bpm', 'body_condition_score'] as const).map((k) => (
                <div key={k} className="space-y-1">
                  <Label>{t(`clinical.f_${k}`)}</Label>
                  <Input type="number" value={exam[k] ?? ''} disabled={!editable} onChange={(e) => setExam({ ...exam, [k]: e.target.value })} onBlur={editable ? onSaveExam : undefined} />
                </div>
              ))}
            </div>
            <Area label={t('clinical.f_cardiovascular_notes')} value={exam.cardiovascular_notes ?? ''} disabled={!editable} onChange={(v) => setExam({ ...exam, cardiovascular_notes: v })} onBlur={editable ? onSaveExam : undefined} />
            <Area label={t('clinical.f_respiratory_notes')} value={exam.respiratory_notes ?? ''} disabled={!editable} onChange={(v) => setExam({ ...exam, respiratory_notes: v })} onBlur={editable ? onSaveExam : undefined} />
            <Area label={t('clinical.f_gastrointestinal_notes')} value={exam.gastrointestinal_notes ?? ''} disabled={!editable} onChange={(v) => setExam({ ...exam, gastrointestinal_notes: v })} onBlur={editable ? onSaveExam : undefined} />
            <Area label={t('clinical.f_abdomen_notes')} value={exam.abdomen_notes ?? ''} disabled={!editable} onChange={(v) => setExam({ ...exam, abdomen_notes: v })} onBlur={editable ? onSaveExam : undefined} />
            <Area label={t('clinical.f_general_impression')} value={exam.general_impression ?? ''} disabled={!editable} onChange={(v) => setExam({ ...exam, general_impression: v })} onBlur={editable ? onSaveExam : undefined} />
            {editable ? <Button size="sm" onClick={onSaveExam}>{t('clinical.save_section')}</Button> : null}
          </div>
        ) : null}

        {tab === 'diagnoses' ? (
          <div className="space-y-2">
            {diagnoses.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('clinical.no_diagnoses')}</p>
            ) : (
              <ul className="space-y-1">
                {diagnoses.map((d) => (
                  <li key={d.id} className="flex items-center justify-between rounded-md border px-2 py-1 text-sm">
                    <span>
                      {d.is_primary ? '★ ' : ''}
                      <strong>{t(`clinical.diagnosis_kind_${d.diagnosis_kind}`)}</strong>: {d.description}
                    </span>
                    {editable && can_diag ? (
                      <Button size="sm" variant="ghost" onClick={() => run(() => removeDiag({ variables: { id: d.id }, context: idempotencyContext() }), t('clinical.toast_diagnosis_removed'))}>
                        {t('clinical.delete')}
                      </Button>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {editable && can_diag ? (
              <div className="space-y-2 rounded-md border p-2">
                <div className="flex gap-2">
                  <select className="border-input bg-background rounded-md border px-2 text-sm" value={newDiag.kind} onChange={(e) => setNewDiag({ ...newDiag, kind: e.target.value })}>
                    {['presumptive', 'differential', 'confirmed', 'rule_out'].map((k) => (
                      <option key={k} value={k}>{t(`clinical.diagnosis_kind_${k}`)}</option>
                    ))}
                  </select>
                  <label className="flex items-center gap-1 text-sm">
                    <input type="checkbox" checked={newDiag.is_primary} onChange={(e) => setNewDiag({ ...newDiag, is_primary: e.target.checked })} />
                    {t('clinical.is_primary')}
                  </label>
                </div>
                <Input placeholder={t('clinical.diagnosis_description')} value={newDiag.description} onChange={(e) => setNewDiag({ ...newDiag, description: e.target.value })} />
                <Button size="sm" onClick={onAddDiagnosis}>{t('clinical.add_diagnosis')}</Button>
              </div>
            ) : null}
          </div>
        ) : null}

        {tab === 'plan' ? (
          <div className="space-y-2">
            {(['treatment_notes', 'medications_notes', 'diet_recommendations', 'activity_restrictions', 'follow_up_notes', 'owner_instructions'] as const).map((k) => (
              <Area key={k} label={t(`clinical.f_${k}`)} value={plan[k] ?? ''} disabled={!editable} onChange={(v) => setPlan({ ...plan, [k]: v })} onBlur={editable ? onSavePlan : undefined} />
            ))}
            {editable ? <Button size="sm" onClick={onSavePlan}>{t('clinical.save_section')}</Button> : null}
          </div>
        ) : null}

        {tab === 'prescriptions' ? (
          <PrescriptionsSection visitId={visit.id} canWrite={can_rx} editable={!locked} appendable={appendable} branding={branding} printBase={rx_print_base} />
        ) : null}

        {tab === 'labs' ? (
          <LabsSection visitId={visit.id} canWrite={can_labs} editable={!locked} appendable={appendable} />
        ) : null}

        {tab === 'imaging' ? (
          <ImagingSection visitId={visit.id} canWrite={can_imaging} editable={!locked} appendable={appendable} />
        ) : null}

        {tab === 'attachments' ? (
          <AttachmentsSection visitId={visit.id} canWrite={can_attach} writable={appendable} />
        ) : null}

        {tab === 'addendums' ? (
          <div className="space-y-2">
            {addendums.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t('clinical.no_addendums')}</p>
            ) : (
              <ul className="space-y-1">
                {addendums.map((ad) => (
                  <li key={ad.id} className="rounded-md border px-2 py-1 text-sm">
                    <p className="text-muted-foreground text-xs">{new Date(ad.created_at).toLocaleString()}</p>
                    <p>{ad.content}</p>
                  </li>
                ))}
              </ul>
            )}
            {ADDENDABLE.has(visit.status) && can_addendum ? (
              <div className="space-y-2">
                <Area label={t('clinical.new_addendum')} value={addendum} onChange={setAddendum} rows={3} />
                <Button size="sm" onClick={onAddAddendum} disabled={addendum.trim().length < 5}>
                  {t('clinical.add_addendum')}
                </Button>
              </div>
            ) : (
              <p className="text-muted-foreground text-xs italic">{t('clinical.addendum_after_close_hint')}</p>
            )}
          </div>
        ) : null}

        {/* Close action — only from in_progress / pending_completion (backend rejects
            closing a fresh draft). Save a section first to move past draft. */}
        {['in_progress', 'pending_completion'].includes(visit.status) && can_close ? (
          <div className="space-y-2 border-t pt-3">
            <Area label={t('clinical.close_no_diagnosis_reason')} value={closeReasons.diag} onChange={(v) => setCloseReasons({ ...closeReasons, diag: v })} rows={2} />
            <Area label={t('clinical.close_no_exam_reason')} value={closeReasons.exam} onChange={(v) => setCloseReasons({ ...closeReasons, exam: v })} rows={2} />
            <Button onClick={onClose}>{t('clinical.close_visit')}</Button>
          </div>
        ) : null}
      </CardContent>
      {showHistory ? (
        <ClinicalHistoryModal pet_id={visit.pet_id} onClose={() => setShowHistory(false)} />
      ) : null}
    </Card>
  );
}
